import type { PortDatumInput } from '../../graphql';
import {
  useCreatePortDataMutation,
  useDeletePortDataMutation,
  useUpdatePortDataMutation,
} from '../../graphql';
import differenceWith from 'lodash/differenceWith';
import intersectionWith from 'lodash/intersectionWith';
import type { WithId, WithNullableId } from '../../lib/util';
import { hasId } from '../../lib/util';

export const isUpdatedPortData = (
  a: Partial<WithNullableId<PortDatumInput>>,
  b: Partial<WithId<PortDatumInput>>,
): boolean =>
  a.id === b.id &&
  (`${a.dischargeRate}` !== `${b.dischargeRate}` ||
    `${a.loadingRate}` !== `${b.loadingRate}` ||
    a.loadingDischargeUnit !== b.loadingDischargeUnit ||
    a.portAgentOrgId !== b.portAgentOrgId ||
    a.bimcoHolidays !== b.bimcoHolidays ||
    a.pilotDeducted !== b.pilotDeducted ||
    a.weatherPermitting !== b.weatherPermitting ||
    a.officeHoursEnd !== b.officeHoursStart ||
    a.laytimeDays !== b.laytimeDays ||
    a.laytimeExclusionEnd !== b.laytimeExclusionEnd ||
    a.laytimeExclusionStart !== b.laytimeExclusionStart ||
    a.laytimeNorTimeLimit !== b.laytimeNorTimeLimit ||
    a.laytimeStartAfterNorTimeLimit !== b.laytimeStartAfterNorTimeLimit ||
    a.laytimeStartBeforeNorTimeLimit !== b.laytimeStartBeforeNorTimeLimit ||
    a.laytimeTrigger !== b.laytimeTrigger);

export const useUpdatePortData = () => {
  const [createPortData] = useCreatePortDataMutation();
  const [updatePortData] = useUpdatePortDataMutation();
  const [deletePortData] = useDeletePortDataMutation();

  const mut = async (
    newPortData: Array<WithNullableId<PortDatumInput>>,
    oldPortData: Array<WithId<PortDatumInput>>,
  ) => {
    const portDataToDelete = differenceWith(
      oldPortData,
      newPortData,
      (a, b) => a.id === b.id,
    );
    const portDataToUpdate = intersectionWith(
      newPortData,
      oldPortData,
      isUpdatedPortData,
    ).filter(hasId);
    const portDataToCreate = differenceWith(
      newPortData,
      oldPortData,
      (a, b) => a.id === b.id,
    );

    await Promise.all(
      portDataToDelete.map((portData) =>
        deletePortData({ variables: { input: { id: portData.id } } }),
      ),
    );
    await Promise.all(
      portDataToUpdate.map(({ id, ...portData }) =>
        updatePortData({
          variables: { input: { id, portDatumPatch: portData } },
        }),
      ),
    );
    const createPortDataResponses = await Promise.all(
      portDataToCreate.map(({ id, ...portData }) =>
        createPortData({
          variables: {
            input: {
              portDatum: {
                ...portData,
              },
            },
          },
        }),
      ),
    );

    return {
      createPortDataResponses,
    };
  };

  return [mut];
};
